/**
 * Live "impact line + primary button label" text for action-preview cards.
 *
 * WHY THIS FILE EXISTS (the guardrail): the card renders a server-driven,
 * fully-shared section schema — adding an action needs zero card code. The ONE
 * exception is the impact line + button label, which recompute from the user's
 * LIVE selection (the server bakes them at propose time with 0 recipients, so
 * they'd go stale the moment someone picks). Recomputing needs domain wording
 * ("violations created" vs "members will receive"). Keeping that wording in
 * per-type `if`s inside the card made it grow every time an action was added.
 *
 * So all of it lives HERE, declaratively: one entry per action that needs
 * bespoke wording, a generic fallback for the rest. The card just calls
 * `summarizeAction()` — no per-type branches in the component. Add an action:
 * add a map entry, or rely on the generic recipient fallback, or return
 * `{ impactPrimary: null }` to defer to the server text entirely.
 */

export interface SummarizerContext {
    /** Live effective recipient count (base items − removed + added). */
    count: number;
    /** True when the card has a live recipients picker to count. */
    hasPicker: boolean;
    /** True when the picker is people (`recipients_people`) vs units. */
    peoplePicker: boolean;
    /** Current toggle edits (e.g. `send_email`). */
    toggles: Record<string, boolean | undefined>;
    /** The server's static primary-button label (baked at propose time). */
    serverButtonLabel: string | null;
}

export interface ActionSummary {
    /** Impact-line override; `null` → keep the server's static text. */
    impactPrimary: string | null;
    /** Primary-button-label override; `null` → keep the server's label. */
    buttonLabel: string | null;
}

const NONE: ActionSummary = { impactPrimary: null, buttonLabel: null };

const plural = (n: number, word: string): string => `${n} ${word}${n !== 1 ? 's' : ''}`;

/** Per-action bespoke wording. Everything else uses the generic fallback. */
const SUMMARIZERS: Record<string, (ctx: SummarizerContext) => ActionSummary> = {
    // Toggle-dependent + count-dependent, so it can't be a plain count template.
    send_violation_notice: ({ count, toggles }) => {
        const sendEmail = toggles['send_email'] ?? true;
        const records = `${plural(count, 'violation record')} created`;
        return {
            impactPrimary: sendEmail
                ? `${plural(count, 'unit')} will receive a violation · ${records}`
                : `${records} · no email will be sent`,
            buttonLabel: `Create ${plural(count, 'violation')}`,
        };
    },
};

/** Any recipient-audience action without a bespoke entry. */
const genericRecipientSummary = (ctx: SummarizerContext): ActionSummary => ({
    impactPrimary: `${plural(ctx.count, ctx.peoplePicker ? 'member' : 'unit')} will receive this`,
    // Refresh the live count INSIDE the server's own label when it carries one
    // (e.g. "Send to 0 units" → "Send to 3 units"). Leaves count-less labels
    // ("Send email") untouched — no per-action wording needed here.
    buttonLabel:
        ctx.serverButtonLabel && /\d/.test(ctx.serverButtonLabel)
            ? ctx.serverButtonLabel.replace(/\d[\d,]*/, String(ctx.count))
            : null,
});

/**
 * The card's single entry point. Returns null overrides (defer to server text)
 * unless there's a live picker with a non-zero selection to summarize.
 */
export function summarizeAction(actionType: string | null, ctx: SummarizerContext): ActionSummary {
    // No live picker, or nothing picked yet → the server's static text (incl.
    // the "pick at least one" prompt) is correct; don't override.
    if (!ctx.hasPicker || ctx.count === 0) {
        return NONE;
    }
    const bespoke = actionType ? SUMMARIZERS[actionType] : undefined;
    return bespoke ? bespoke(ctx) : genericRecipientSummary(ctx);
}
